/**
 * Shared utilities for handling drag-and-drop of files and folders
 * Preserves folder structure by setting webkitRelativePath on each File
 */

/**
 * Recursively read all files from a dropped directory entry.
 * Manually sets webkitRelativePath on each file to preserve folder structure.
 */
async function readDirectoryRecursive(
  dirEntry: FileSystemDirectoryEntry,
  basePath: string,
  files: File[]
): Promise<void> {
  const reader = dirEntry.createReader();

  // Read all entries in this directory
  const allEntries: FileSystemEntry[] = [];

  const readBatch = async (): Promise<void> => {
    const entries = await new Promise<FileSystemEntry[]>((resolve, reject) => {
      reader.readEntries(
        (entries) => resolve(entries),
        (error) => reject(error)
      );
    });

    if (entries.length > 0) {
      allEntries.push(...entries);
      await readBatch(); // Keep reading until empty
    }
  };

  await readBatch();

  for (const entry of allEntries) {
    const fullPath = basePath ? `${basePath}/${entry.name}` : entry.name;

    if (entry.isFile) {
      // Get the File object for this entry
      const file = await new Promise<File>((resolve, reject) => {
        (entry as FileSystemFileEntry).file(
          (file) => {
            // Manually set webkitRelativePath to preserve folder structure
            Object.defineProperty(file, 'webkitRelativePath', {
              value: fullPath,
              writable: false,
              configurable: true
            });
            resolve(file);
          },
          (error) => reject(error)
        );
      });
      files.push(file);
    } else if (entry.isDirectory) {
      // Recursively process subdirectories
      await readDirectoryRecursive(
        entry as FileSystemDirectoryEntry,
        fullPath,
        files
      );
    }
  }
}

const getFileFingerprint = (file: File): string => {
  const relativePath = file.webkitRelativePath || file.name;
  return JSON.stringify([relativePath, file.size, file.lastModified, file.type]);
};

const getSourceFileFingerprint = (file: File): string =>
  JSON.stringify([file.name, file.size, file.lastModified, file.type]);

const logEmptyDrop = (dataTransfer: DataTransfer) => {
  const items = [];
  for (let index = 0; index < dataTransfer.items.length; index++) {
    const item = dataTransfer.items[index];
    items.push({ kind: item.kind, type: item.type });
  }

  console.warn('[DragDrop] No files could be extracted from the drop', {
    types: Array.from(dataTransfer.types),
    items,
    fileCount: dataTransfer.files.length,
  });
};

/**
 * Extract files and folders from a complete DataTransfer while its protected
 * data store is still readable. Outlook and other applications can expose
 * virtual files through dataTransfer.files even when dataTransfer.items
 * contains string items or items that cannot produce a FileSystemEntry.
 */
export async function processDroppedDataTransfer(dataTransfer: DataTransfer): Promise<File[]> {
  const files: File[] = [];
  const fallbackFiles: File[] = [];
  const seenFiles = new Set<string>();
  const directoryNames = new Set<string>();
  const directoryEntries: FileSystemDirectoryEntry[] = [];
  const fileEntries: FileSystemFileEntry[] = [];
  const traversedDirectoryFiles: File[] = [];

  const addFile = (file: File) => {
    const fingerprint = getFileFingerprint(file);
    if (!seenFiles.has(fingerprint)) {
      seenFiles.add(fingerprint);
      files.push(file);
    }
  };

  try {
    // Snapshot files before the drag data store becomes protected again.
    // Reconcile them after item processing because browsers may also include
    // directory placeholders or flattened copies of directory children here.
    for (let index = 0; index < dataTransfer.files.length; index++) {
      fallbackFiles.push(dataTransfer.files[index]);
    }

    // Capture item data synchronously before the drag data store becomes
    // protected again. Directory traversal can then happen asynchronously.
    for (let index = 0; index < dataTransfer.items.length; index++) {
      const item = dataTransfer.items[index];
      if (item.kind !== 'file') {
        continue;
      }

      let entry: FileSystemEntry | null = null;
      try {
        entry = item.webkitGetAsEntry?.() ?? null;
      } catch (error) {
        console.warn('[DragDrop] Failed to read a dropped item entry', error);
      }

      if (entry?.isDirectory) {
        directoryNames.add(entry.name);
        directoryEntries.push(entry as FileSystemDirectoryEntry);
        continue;
      }

      let file: File | null = null;
      try {
        file = item.getAsFile();
      } catch (error) {
        console.warn('[DragDrop] Failed to read a dropped item as a file', error);
      }

      if (file) {
        addFile(file);
      } else if (entry?.isFile) {
        fileEntries.push(entry as FileSystemFileEntry);
      }
    }

    for (const entry of fileEntries) {
      try {
        const file = await new Promise<File>((resolve, reject) => {
          entry.file(resolve, reject);
        });
        addFile(file);
      } catch (error) {
        console.warn('[DragDrop] Failed to read a dropped file entry', error);
      }
    }

    for (const entry of directoryEntries) {
      try {
        const directoryFiles: File[] = [];
        await readDirectoryRecursive(entry, entry.name, directoryFiles);
        traversedDirectoryFiles.push(...directoryFiles);
        directoryFiles.forEach(addFile);
      } catch (error) {
        console.warn('[DragDrop] Failed to read a dropped directory', error);
      }
    }

    // dataTransfer.files is the fallback for Outlook virtual files, but can
    // duplicate directory data. Match copies as a multiset so two legitimate
    // same-named files in different folders remain distinct.
    const isDirectoryPlaceholder = (file: File) =>
      directoryNames.has(file.name) && file.size === 0 && file.type === '';
    const hasDirectoryPlaceholders = fallbackFiles.some(isDirectoryPlaceholder);
    const representedFileCounts = new Map<string, number>();
    if (!hasDirectoryPlaceholders) {
      for (const file of traversedDirectoryFiles) {
        const fingerprint = getSourceFileFingerprint(file);
        representedFileCounts.set(fingerprint, (representedFileCounts.get(fingerprint) ?? 0) + 1);
      }
    }

    for (const file of fallbackFiles) {
      if (isDirectoryPlaceholder(file)) {
        continue;
      }

      const fingerprint = getSourceFileFingerprint(file);
      const representedCount = representedFileCounts.get(fingerprint) ?? 0;
      if (representedCount > 0) {
        representedFileCounts.set(fingerprint, representedCount - 1);
        continue;
      }

      addFile(file);
    }

    if (files.length === 0) {
      logEmptyDrop(dataTransfer);
    }

    return files;
  } catch (error) {
    console.error('Error processing dropped items:', error);
    throw error;
  }
}
